import { alpha } from "@mui/material/styles";

// ----------------------------------------------------------------------

export function overrides(theme) {
  return {
    MuiCssBaseline: {
      styleOverrides: {
        "*": {
          boxSizing: "border-box",
        },
        html: {
          margin: 0,
          padding: 0,
          width: "100%",
          height: "100%",
          WebkitOverflowScrolling: "touch",
        },
        body: {
          margin: 0,
          padding: 0,
          width: "100%",
          height: "100%",
        },

        "#root": {
          width: "100%",
          height: "100%",
        },
        input: {
          "&[type=number]": {
            MozAppearance: "textfield",
            "&::-webkit-outer-spin-button": {
              margin: 0,
              WebkitAppearance: "none",
            },
            "&::-webkit-inner-spin-button": {
              margin: 0,
              WebkitAppearance: "none",
            },
          },
        },
        img: {
          maxWidth: "100%",
          display: "inline-block",
          verticalAlign: "bottom",
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          color: "#ccc",
        },
        colorPrimary: {
          "&.Mui-checked": {
            color: "#007BFF",
          },
        },
        track: {
          opacity: 0.2,
          backgroundColor: "grey",
          ".Mui-checked.Mui-checked + &": {
            opacity: 0.7,
            backgroundColor: "grey",
          },
        },
      },
    },
    MuiBackdrop: {
      styleOverrides: {
        root: {
          backgroundColor: alpha(theme.palette.grey[900], 0.8),
        },
        invisible: {
          background: "transparent",
        },
      },
    },
    MuiButton: {
      variants: [
        {
          props: { variant: "primary" },
          style: {
            background: "rgba(106,116,123, .1)",
            color: "#6a747b",
            fontSize: "14px",
            fontWeight: "300",
            padding: "4px 8px",
            borderRadius: "5px",
            "&:hover": {
              backgroundColor: "#6a747b",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "secondary" },
          style: {
            fontWeight: "600",
            borderRadius: "4px",
            padding: "9px 16px",
            "&:hover": {
              background: "#f7f7f7",
              color: "#455a64",
            },
          },
        },
        {
          props: { variant: "secondary_active" },
          style: {
            fontWeight: "600",
            borderRadius: "4px",
            padding: "9px 16px",
            background: "#455a64",
            color: "#fff",
            "&:hover": {
              background: "#455a64",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "contained" },
          style: {
            background: "#FFD700",
            color: "#fff",
            fontSize: "14px",
            fontWeight: "300",
            padding: "5px 20px",
            borderRadius: "5px",
            ":hover": {
              background: "#FFD700",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "outlined" },
          style: {
            border: `1px solid  ${"#007bff"}`,
            background: "transparent",
            color: "#007bff",
            fontSize: "14px",
            fontWeight: "300",
            padding: "5px 20px",
            borderRadius: "5px",
            ":hover": {
              background: "#007bff",
              color: "#fff",
              border: `1px solid  ${"#007bff"}`,
            },
          },
        },
        {
          props: { variant: "tertiary" },
          style: {
            minHeight: 36,
            background: "rgba(40,110,251,.1)",
            color: "#286efb",
            fontSize: "14px",
            fontWeight: "300",
            padding: "5px 10px",
            borderRadius: "5px",
            ":hover": {
              background: "#286efb",
              color: "#fff",
            },
          },
        },
        {
          props: { variant: "quaternary" },
          style: {
            minHeight: 36,
            background: "#fff",
            color: "#455a64",
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 15px",
            borderRadius: "0px",

            ":hover": {
              background: "#fff",
              color: "#67757c",
            },
          },
        },

        {
          props: { variant: "danger" },
          style: {
            minHeight: 36,
            background: "#fff",
            color: "#DC3545",
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 15px",
            borderRadius: "8px",
            ":hover": {
              background: "#DC3545",
              color: "#fff",
            },
          },
        },

        {
          props: { variant: "success" },
          style: {
            background: "#DCE9E0",
            color: "#28A745",

            fontSize: "14px",
            fontWeight: "bold",
            padding: "4px 15px",
            borderRadius: "8px",
            ":hover": {
              background: "#28A745",
              color: "#DCE9E0",
            },
          },
        },
      ],
      styleOverrides: {
        containedInherit: {
          color: theme.palette.common.white,
          backgroundColor: theme.palette.grey[800],
          "&:hover": {
            color: theme.palette.common.white,
            backgroundColor: theme.palette.grey[800],
          },
        },
        sizeLarge: {
          minHeight: 48,
        },
      },
    },

    MuiCard: {
      styleOverrides: {
        root: {
          boxShadow: theme.customShadows.card,
          borderRadius: "4px",
          position: "relative",
          zIndex: 0, // Fix Safari overflow: hidden with border radius
        },
      },
    },
    MuiCardHeader: {
      defaultProps: {
        titleTypographyProps: { variant: "h6" },
        subheaderTypographyProps: { variant: "body2" },
      },
      styleOverrides: {
        root: {
          padding: theme.spacing(3, 3, 0),
        },
      },
    },
    MuiOutlinedInput: {
      variants: [
        {
          props: { variant: "primary" },
          style: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: alpha(theme.palette.primary.main, 0.24),
              borderRadius: "2px",
              backgroundColor: "#fff",
            },
          },
        },
        {
          props: { variant: "custom" },
          style: {
            "& .MuiOutlinedInput-notchedOutline": {
              borderColor: "your-custom-color",
              borderRadius: "your-custom-radius",
              backgroundColor: "your-custom-background-color",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          "& .MuiOutlinedInput-notchedOutline": {
            borderColor: alpha(theme.palette.grey[500], 0.24),
            borderRadius: "10px",
          },
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },

          ".Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#000",
          },
        },
      },
    },
    MuiInputBase: {
      styleOverrides: {
        input: {
          color: "#000",
          "&::placeholder": {
            color: "gray",
          },
          borderRadius: "4px",
        },
      },
    },
    MuiTextField: {
      styleOverrides: {
        root: {
          "& label": {
            color: "#3E68A8",
          },
          "& label.Mui-focused": {
            color: "#3E68A8",
          },
          "& .MuiInput-underline:after": {
            borderBottomColor: "#3E68A8",
          },

          "& .MuiOutlinedInput-root": {
            "& fieldset": {
              borderColor: "#3E68A8",
            },
            "&:hover fieldset": {
              borderColor: "#3E68A8",
            },
            "&.Mui-focused fieldset": {
              borderColor: "#3E68A8",
            },
          },
        },
      },
    },

    MuiPaper: {
      defaultProps: {
        elevation: 0,
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "row",
          gap: "5px",
          padding: "10px 25px",
          minHeight: "auto",
        },
      },
    },

    MuiTableCell: {
      styleOverrides: {
        head: {
          color: theme.palette.text.secondary,
          backgroundColor: theme.palette.background.neutral,
        },
      },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          backgroundColor: theme.palette.grey[800],
        },
        arrow: {
          color: theme.palette.grey[800],
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: "#ddd",
        },
        colorPrimary: {
          "&.Mui-checked": {
            color: "#007BFF",
          },
        },
        track: {
          opacity: 0.2,
          backgroundColor: "grey",
          ".Mui-checked.Mui-checked + &": {
            opacity: 0.7,
            backgroundColor: "grey",
          },
        },
      },
    },
    MuiTypography: {
      variants: [
        {
          props: { variant: "H1" },
          style: {
            fontWeight: 700,
            fontFamily: "'Montserrat', sans-serif",
            "@media (max-width: 1920px)": {
              fontSize: "50px",
            },
            "@media (max-width: 1280px)": {
              fontSize: "40px",
            },
            "@media (max-width: 960px)": {
              fontSize: "30px",
            },
            "@media (max-width: 600px)": {
              fontSize: "20px",
            },
          },
        },
        {
          props: { variant: "H2" },

          style: {
            fontWeight: 600,
            fontFamily: "'Montserrat', sans-serif",

            "@media (max-width: 1920px)": {
              fontSize: "30px",
            },
            "@media (max-width: 1280px)": {
              fontSize: "25px",
            },
            "@media (max-width: 960px)": {
              fontSize: "22px",
            },
            "@media (max-width: 600px)": {
              fontSize: "16px",
            },
          },
        },

        {
          props: { variant: "H3" },

          style: {
            fontWeight: 600,
            fontFamily: "'Montserrat', sans-serif",

            "@media (max-width: 1920px)": {
              fontSize: "20px",
            },
            "@media (max-width: 1280px)": {
              fontSize: "18px",
            },
            "@media (max-width: 960px)": {
              fontSize: "16px",
            },
            "@media (max-width: 600px)": {
              fontSize: "14px",
            },
          },
        },

        {
          props: { variant: "P1" },
          style: {
            fontWeight: 400,
            "@media (max-width: 1920px)": {
              fontSize: "20px",
            },
            "@media (max-width: 1280px)": {
              fontSize: "18px",
            },
            "@media (max-width: 960px)": {
              fontSize: "16px",
            },
            "@media (max-width: 600px)": {
              fontSize: "14px",
            },
          },
        },
        {
          props: { variant: "P2" },
          style: {
            fontWeight: 500,
            fontFamily: "'Montserrat', sans-serif",

            "@media (max-width: 1920px)": {
              fontSize: "18px",
            },
            "@media (max-width: 1280px)": {
              fontSize: "16px",
            },
            "@media (max-width: 960px)": {
              fontSize: "14px",
            },
            "@media (max-width: 600px)": {
              fontSize: "12px",
            },
          },
        },
        {
          props: { variant: "P3" },
          style: {
            fontWeight: 400,
            fontFamily: "'Montserrat', sans-serif",

            "@media (max-width: 1920px)": {
              fontSize: "16px",
            },
            "@media (max-width: 1280px)": {
              fontSize: "14px",
            },
            "@media (max-width: 960px)": {
              fontSize: "12px",
            },
            "@media (max-width: 600px)": {
              fontSize: "10px",
            },
          },
        },
        {
          props: { variant: "paragraph" },
          style: { fontSize: "0.9rem", color: "#797979", fontWeight: 600 },
        },
      ],
      styleOverrides: {
        paragraph: {
          marginBottom: theme.spacing(2),
        },
        gutterBottom: {
          marginBottom: theme.spacing(1),
        },
      },
    },
    MuiMenuItem: {
      styleOverrides: {
        root: {
          ...theme.typography.body2,
        },
      },
    },

    MuiSelect: {
      styleOverrides: {
        root: {
          borderRadius: "3px",
          height: "auto",
          fontSize: "14px",
          width: "100%",
          ".css-l9jeop-MuiFormLabel-root-MuiInputLabel-root.Mui-focused": {
            color: "red !important",
          },
          ".MuiOutlinedInput-notchedOutline": {
            borderColor: "#3E68A8",
          },
          "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
            borderColor: "#3E68A8",
          },
          "&:hover .MuiOutlinedInput-notchedOutline": {
            borderColor: "#3E68A8",
          },
          ".MuiSvgIcon-root ": {
            fill: "#3E68A8",
          },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: "red",
          "&.Mui-focused": {
            color: "red",
          },
        },
      },
    },
    MuiIconButton: {
      variants: [
        {
          props: { variant: "danger" },
          style: {
            minHeight: 36,
            background: "#fff",
            color: "#DC3545",
            fontSize: "14px",
            fontWeight: "bold",
            padding: "10px 15px",
            borderRadius: "8px",
            ":hover": {
              background: "#DC3545",
              color: "#fff",
            },
          },
        },
      ],
      styleOverrides: {
        root: {
          bgcolor: "#f8d7da",
          color: "#721c24",
          borderRadius: "6px",
          ":hover": { bgcolor: "#c82333", color: "#fff" },
        },
      },
    },
  };
}
